import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface Project {
  id: number;
  project_code: string;
  project_name: string;
  description?: string;
  client_name?: string;
  start_date?: string;
  end_date?: string;
  status: string;
  properties: any;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface Section {
  id: number;
  section_code: string;
  section_name: string;
  description?: string;
  project_id: string;
  project_name?: string;
  project_code?: string;
  start_chainage: number;
  end_chainage: number;
  total_length: number;
  geometry: any;
  properties: any;
  numbering_config: any;
  created_at: string;
  updated_at: string;
  created_by: string;
  status: string;
}

export interface Segment {
  id: number;
  segment_code: string;
  segment_name: string;
  description?: string;
  section_id: number;
  section_name?: string;
  section_code?: string;
  start_chainage: number;
  end_chainage: number;
  length: number;
  geometry: any;
  properties: any;
  numbering_config: any;
  display_offset: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  status: string;
}

export interface Subsegment {
  id: number;
  subsegment_code: string;
  subsegment_name: string;
  description?: string;
  segment_id: number;
  segment_name?: string;
  segment_code?: string;
  section_id: number;
  section_name?: string;
  section_code?: string;
  start_chainage: number;
  end_chainage: number;
  length: number;
  geometry: any;
  properties: any;
  numbering_config: any;
  display_offset: number;
  rule_based: boolean;
  rule_config: any;
  created_at: string;
  updated_at: string;
  created_by: string;
  status: string;
}

export interface NumberingRule {
  id: number;
  rule_name: string;
  rule_type: 'section' | 'segment' | 'subsegment';
  parent_type?: string;
  prefix: string;
  start_number: number;
  increment: number;
  direction: 'asc' | 'desc';
  format_pattern: string;
  conditions: any;
  created_at: string;
  updated_at: string;
  created_by: string;
  status: string;
}

export interface Junction {
  id: number;
  junction_code: string;
  junction_name: string;
  description?: string;
  geometry: any;
  affected_segments: number[];
  conflict_type: string;
  resolution_method: string;
  properties: any;
  created_at: string;
  updated_at: string;
  created_by: string;
  status: string;
}

export interface ProjectStats {
  sections_count: number;
  segments_count: number;
  subsegments_count: number;
  junctions_count: number;
  total_length: number;
}

export interface SplitResult {
  new_segment_id: number;
  new_subsegment_ids: number[];
}

export interface ConflictDetection {
  junction_id: number;
  conflict_type: string;
  affected_segments: number[];
}

@Injectable({
  providedIn: 'root'
})
export class RoadProjectService {
  private apiUrl = `${environment.BASE_URL}/road-project`;

  constructor(private http: HttpClient) {}

  // Project Management
  getProjects(): Observable<{ success: boolean; data: Project[]; message: string }> {
    return this.http.get<{ success: boolean; data: Project[]; message: string }>(`${this.apiUrl}/projects`);
  }

  createProject(project: Partial<Project>): Observable<{ success: boolean; data: Project; message: string }> {
    return this.http.post<{ success: boolean; data: Project; message: string }>(`${this.apiUrl}/projects`, project);
  }

  getProjectStats(projectId: string): Observable<{ success: boolean; data: ProjectStats; message: string }> {
    return this.http.get<{ success: boolean; data: ProjectStats; message: string }>(`${this.apiUrl}/projects/${projectId}/stats`);
  }

  // Section Management
  getSections(projectId: string, bbox?: string, limit: number = 1000): Observable<{ success: boolean; data: any; message: string }> {
    let params = new HttpParams().set('limit', limit.toString());
    if (bbox) {
      params = params.set('bbox', bbox);
    }
    return this.http.get<{ success: boolean; data: any; message: string }>(`${this.apiUrl}/projects/${projectId}/sections`, { params });
  }

  createSection(section: Partial<Section>): Observable<{ success: boolean; data: any; message: string }> {
    return this.http.post<{ success: boolean; data: any; message: string }>(`${this.apiUrl}/projects/${section.project_id}/sections`, section);
  }

  // Segment Management
  getSegments(sectionId: number, bbox?: string, limit: number = 1000): Observable<{ success: boolean; data: any; message: string }> {
    let params = new HttpParams().set('limit', limit.toString());
    if (bbox) {
      params = params.set('bbox', bbox);
    }
    return this.http.get<{ success: boolean; data: any; message: string }>(`${this.apiUrl}/sections/${sectionId}/segments`, { params });
  }

  createSegment(segment: Partial<Segment>): Observable<{ success: boolean; data: any; message: string }> {
    return this.http.post<{ success: boolean; data: any; message: string }>(`${this.apiUrl}/sections/${segment.section_id}/segments`, segment);
  }

  splitSegment(segmentId: number, splitPoint: any): Observable<{ success: boolean; data: SplitResult; message: string }> {
    return this.http.post<{ success: boolean; data: SplitResult; message: string }>(`${this.apiUrl}/segments/${segmentId}/split`, { splitPoint });
  }

  // Subsegment Management
  getSubsegments(segmentId: number, bbox?: string, limit: number = 1000): Observable<{ success: boolean; data: any; message: string }> {
    let params = new HttpParams().set('limit', limit.toString());
    if (bbox) {
      params = params.set('bbox', bbox);
    }
    return this.http.get<{ success: boolean; data: any; message: string }>(`${this.apiUrl}/segments/${segmentId}/subsegments`, { params });
  }

  createSubsegment(subsegment: Partial<Subsegment>): Observable<{ success: boolean; data: any; message: string }> {
    return this.http.post<{ success: boolean; data: any; message: string }>(`${this.apiUrl}/segments/${subsegment.segment_id}/subsegments`, subsegment);
  }

  applySubsegmentRules(segmentId: number): Observable<{ success: boolean; data: { subsegment_count: number }; message: string }> {
    return this.http.post<{ success: boolean; data: { subsegment_count: number }; message: string }>(`${this.apiUrl}/segments/${segmentId}/apply-rules`, {});
  }

  // Numbering Rules Management
  getNumberingRules(ruleType?: string): Observable<{ success: boolean; data: NumberingRule[]; message: string }> {
    let params = new HttpParams();
    if (ruleType) {
      params = params.set('ruleType', ruleType);
    }
    return this.http.get<{ success: boolean; data: NumberingRule[]; message: string }>(`${this.apiUrl}/numbering-rules`, { params });
  }

  createNumberingRule(rule: Partial<NumberingRule>): Observable<{ success: boolean; data: NumberingRule; message: string }> {
    return this.http.post<{ success: boolean; data: NumberingRule; message: string }>(`${this.apiUrl}/numbering-rules`, rule);
  }

  // Display Offset Management
  updateDisplayOffset(id: number, type: 'segment' | 'subsegment', displayOffset: number): Observable<{ success: boolean; data: any; message: string }> {
    return this.http.put<{ success: boolean; data: any; message: string }>(`${this.apiUrl}/display-offset`, {
      id,
      type,
      display_offset: displayOffset
    });
  }

  // Conflict Detection
  detectConflicts(): Observable<{ success: boolean; data: ConflictDetection[]; message: string }> {
    return this.http.get<{ success: boolean; data: ConflictDetection[]; message: string }>(`${this.apiUrl}/conflicts`);
  }

  // Utility Methods
  generateSectionCode(projectCode: string, startChainage: number): string {
    return `${projectCode}_SEC_${Math.floor(startChainage).toString().padStart(6, '0')}`;
  }

  generateSegmentCode(sectionCode: string, startChainage: number): string {
    return `${sectionCode}_SEG_${Math.floor(startChainage).toString().padStart(6, '0')}`;
  }

  generateSubsegmentCode(segmentCode: string, startChainage: number): string {
    return `${segmentCode}_SUB_${Math.floor(startChainage).toString().padStart(6, '0')}`;
  }

  // Auto-numbering methods
  applyAutoNumbering(features: any[], rule: NumberingRule): any[] {
    const numberedFeatures = [...features];
    let currentNumber = rule.start_number;

    if (rule.direction === 'desc') {
      numberedFeatures.reverse();
    }

    numberedFeatures.forEach((feature, index) => {
      const number = currentNumber + (index * rule.increment);
      const formattedNumber = this.formatNumber(number, rule.format_pattern);
      const code = `${rule.prefix}_${formattedNumber}`;

      feature.properties.number = number;
      feature.properties.code = code;
      feature.properties.formatted_code = rule.format_pattern.replace('{number}', formattedNumber);
    });

    return numberedFeatures;
  }

  private formatNumber(number: number, pattern: string): string {
    const match = pattern.match(/\{number:(\d+)d\}/);
    if (match) {
      const digits = parseInt(match[1]);
      return number.toString().padStart(digits, '0');
    }
    return number.toString();
  }

  // Geometry utility methods
  calculateLength(geometry: any): number {
    if (!geometry || !geometry.coordinates) return 0;

    let length = 0;
    const coords = geometry.coordinates;

    for (let i = 1; i < coords.length; i++) {
      const [lng1, lat1] = coords[i - 1];
      const [lng2, lat2] = coords[i];
      length += this.haversineDistance(lat1, lng1, lat2, lng2);
    }

    return length;
  }

  private haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLng = this.toRadians(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  // Display offset calculation
  calculateDisplayOffset(geometry: any, offset: number): any {
    if (!geometry || !geometry.coordinates) return geometry;

    const offsetGeometry = { ...geometry };
    offsetGeometry.coordinates = geometry.coordinates.map((coord: number[]) => {
      // Simple offset calculation - you can implement more sophisticated offset logic
      return [coord[0] + offset * 0.0001, coord[1] + offset * 0.0001];
    });

    return offsetGeometry;
  }

  // Conflict resolution methods
  resolveJunctionConflict(junction: Junction, resolutionMethod: string): any {
    switch (resolutionMethod) {
      case 'priority':
        return this.resolveByPriority(junction);
      case 'length':
        return this.resolveByLength(junction);
      case 'manual':
        return this.resolveManually(junction);
      default:
        return junction;
    }
  }

  private resolveByPriority(junction: Junction): Junction {
    // Implement priority-based resolution
    return junction;
  }

  private resolveByLength(junction: Junction): Junction {
    // Implement length-based resolution
    return junction;
  }

  private resolveManually(junction: Junction): Junction {
    // Implement manual resolution
    return junction;
  }

  // Export methods
  exportProjectData(projectId: string, format: 'geojson' | 'shapefile' | 'kml' = 'geojson'): Observable<{ success: boolean; data: any; message: string }> {
    return this.http.get<{ success: boolean; data: any; message: string }>(`${this.apiUrl}/projects/${projectId}/export`, {
      params: new HttpParams().set('format', format)
    });
  }

  // Import methods
  importProjectData(projectId: string, data: any, format: 'geojson' | 'shapefile' | 'kml' = 'geojson'): Observable<{ success: boolean; data: any; message: string }> {
    return this.http.post<{ success: boolean; data: any; message: string }>(`${this.apiUrl}/projects/${projectId}/import`, {
      data,
      format
    });
  }

  // Validation methods
  validateGeometry(geometry: any): boolean {
    if (!geometry || !geometry.type || !geometry.coordinates) {
      return false;
    }

    const validTypes = ['Point', 'LineString', 'Polygon', 'MultiPolygon'];
    if (!validTypes.includes(geometry.type)) {
      return false;
    }

    return true;
  }

  validateChainage(startChainage: number, endChainage: number): boolean {
    return startChainage >= 0 && endChainage > startChainage;
  }

  validateNumberingRule(rule: Partial<NumberingRule>): boolean {
    if (!rule.rule_name || !rule.rule_type) {
      return false;
    }

    if (rule.start_number < 0 || rule.increment < 1) {
      return false;
    }

    if (rule.direction && !['asc', 'desc'].includes(rule.direction)) {
      return false;
    }

    return true;
  }
}

